import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Edit, Trash2, X, UserPlus, Save } from 'lucide-react';

const Executives = () => {
    const [executives, setExecutives] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        designation: '',
        email: ''
    });

    const fetchExecutives = async () => {
        try {
            const res = await api.get('/executives');
            console.log('Executives List Response:', res.data.data);
            setExecutives(res.data.data);
            setLoading(false);
        } catch (err) {
            console.error('Failed to fetch executives', err);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchExecutives();
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSave = async () => {
        if (!formData.name || !formData.email) {
            alert('Please enter Name and Email');
            return;
        }
        console.log('Saving Executive:', { editingId, formData });
        try {
            if (editingId) {
                // Update existing
                console.log('Sending PUT request to:', `/executives/${editingId}`);
                const res = await api.put(`/executives/${editingId}`, formData);
                console.log('Update Response:', res.data);
                if (res.data.status === 'success') {
                    alert('Executive updated successfully!');
                    setEditingId(null);
                    setFormData({ name: '', phone: '', designation: '', email: '' });
                    fetchExecutives();
                }
            } else {
                // Create new
                console.log('Sending POST request to:', '/executives');
                const res = await api.post('/executives', formData);
                console.log('Create Response:', res.data);
                if (res.data.status === 'success') {
                    alert('Executive added successfully!');
                    setFormData({ name: '', phone: '', designation: '', email: '' });
                    fetchExecutives();
                }
            }
        } catch (err) {
            console.error('Failed to save executive. Full Error:', err);
            alert('Error saving executive');
        }
    };

    const handleEdit = (executive) => {
        const id = executive.id || executive._id;
        console.log('Editing Executive:', { id, executive });
        setEditingId(id);
        setFormData({
            name: executive.name,
            phone: executive.phone || '',
            designation: executive.designation || '',
            email: executive.email
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setFormData({ name: '', phone: '', designation: '', email: '' });
    };

    const handleDelete = async (id) => {
        console.log('Deleting Executive ID:', id);
        if (!window.confirm('Are you sure you want to delete this executive account?')) return;
        try {
            const res = await api.delete(`/executives/${id}`);
            console.log('Delete Response:', res.data);
            if (res.data.status === 'success') {
                fetchExecutives();
            }
        } catch (err) {
            console.error('Failed to delete executive. Full Error:', err);
            alert('Error deleting executive');
        }
    };

    return (
        <div className="p-4 md:p-10 max-w-[1400px] mx-auto">
            <h1 className="text-2xl md:text-[2.2rem] font-[900] text-primary mb-10 tracking-tight">Sales Executives</h1>

            {/* Creation/Edit Form */}
            <div className={`bg-white rounded-2xl border-2 ${editingId ? 'border-amber-200' : 'border-slate-200'} mb-10 overflow-hidden shadow-sm transition-all`}>
                <div className={`section-header ${editingId ? 'bg-amber-50/50' : ''}`}>
                    <div className="flex items-center gap-3">
                        {editingId ? <Edit size={20} className="text-amber-600" /> : <UserPlus size={20} className="text-primary" />}
                        <h3>{editingId ? 'Modify Executive Account' : 'Create New Executive'}</h3>
                    </div>
                    {editingId && (
                        <button
                            onClick={handleCancelEdit}
                            className="text-slate-400 hover:text-slate-600 p-2 hover:bg-white rounded-lg transition-all"
                        >
                            <X size={20} />
                        </button>
                    )}
                </div>
                <div className="grid-partitioned grid-cols-1 md:grid-cols-4">
                    <div className="p-8 group hover:bg-slate-50/50 transition-colors">
                        <label className="block text-[11px] font-[800] text-slate-400 mb-3 uppercase tracking-[0.1em]">Full Name</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="e.g. John Doe"
                            className="input-field"
                        />
                    </div>
                    <div className="p-8 group hover:bg-slate-50/50 transition-colors">
                        <label className="block text-[11px] font-[800] text-slate-400 mb-3 uppercase tracking-[0.1em]">Contact Number</label>
                        <input
                            type="text"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="e.g. +971..."
                            className="input-field"
                        />
                    </div>
                    <div className="p-8 group hover:bg-slate-50/50 transition-colors">
                        <label className="block text-[11px] font-[800] text-slate-400 mb-3 uppercase tracking-[0.1em]">Job Designation</label>
                        <input
                            type="text"
                            name="designation"
                            value={formData.designation}
                            onChange={handleChange}
                            placeholder="e.g. Sales Manager"
                            className="input-field"
                        />
                    </div>
                    <div className="p-8 group hover:bg-slate-50/50 transition-colors">
                        <label className="block text-[11px] font-[800] text-slate-400 mb-3 uppercase tracking-[0.1em]">Email Address</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="e.g. john@company.com"
                            className="input-field"
                        />
                    </div>
                </div>
                <div className="p-6 bg-slate-50/30 border-t-2 border-slate-100 flex justify-end gap-4 px-10">
                    {editingId && (
                        <button
                            className="px-8 py-3 rounded-lg font-bold text-slate-500 hover:bg-white transition-all"
                            onClick={handleCancelEdit}
                        >
                            Discard Changes
                        </button>
                    )}
                    <button className={editingId ? "bg-amber-600 text-white font-bold py-3 px-8 rounded-lg hover:bg-amber-700 transition-all shadow-sm flex items-center gap-2" : "btn-primary flex items-center gap-2"} onClick={handleSave}>
                        {editingId ? <><Save size={18} /> Update Account</> : <><UserPlus size={18} /> Create Account</>}
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-2xl p-1 md:p-1 border-2 border-slate-200 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="data-table !border-none !rounded-none">
                        <thead>
                            <tr>
                                <th>Sales Manager / Executive Name</th>
                                <th>Designation</th>
                                <th>Email Address</th>
                                <th>Contact Number</th>
                                <th className="text-center !border-r-0">Management</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? <tr><td colSpan="5" className="p-10 text-center font-bold text-slate-400 italic">Fetching executive records...</td></tr> :
                                executives.map(e => (
                                    <tr key={e.id || e._id} className={editingId === (e.id || e._id) ? 'bg-amber-50/30' : ''}>
                                        <td data-label="Name"><span className="font-[800] text-primary">{e.name}</span></td>
                                        <td data-label="Designation"><span className="font-medium text-slate-500">{e.designation}</span></td>
                                        <td data-label="Email"><span className="text-slate-600 font-medium underline underline-offset-4 decoration-slate-200">{e.email}</span></td>
                                        <td data-label="Phone" className="font-bold text-slate-700">{e.phone}</td>
                                        <td className="!border-r-0" data-label="Actions">
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    onClick={() => handleEdit(e)}
                                                    className="p-2 hover:bg-primary hover:text-white rounded-lg text-primary transition-all border border-slate-100 hover:border-primary"
                                                    title="Edit Account"
                                                >
                                                    <Edit size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(e.id || e._id)}
                                                    className="p-2 hover:bg-red-600 hover:text-white rounded-lg text-red-500 transition-all border border-slate-100 hover:border-red-600"
                                                    title="Delete Account"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            }
                            {!loading && executives.length === 0 && (
                                <tr><td colSpan="5" className="text-center p-12 text-slate-400 font-bold italic">No executive accounts found in database</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default Executives;
